import { Brain, BarChart3, Users, Sparkles, CheckCircle, MessageCircle, TrendingUp } from '../icons';
import { Badge } from '../ui/badge';

const benefits = [
  {
    id: 'ai-companion',
    badge: 'AI-Powered',
    title: 'Mindful AI Companion',
    description:
      'Our AI analyzes your responses and provides personalized insights to help you understand your thoughts and behaviors.',
    features: [
      { icon: MessageCircle, label: 'Personal Growth' },
      { icon: Brain, label: 'Deep Insights' },
    ],
    image:
      'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=600',
    imageAlt: 'AI Analysis Dashboard',
    reverse: false,
  },
  {
    id: 'metrics',
    badge: 'Analytics',
    title: 'Personality Metrics',
    description:
      'Get gentle, actionable advice tailored to your needs — from understanding your 12 key characteristics to tracking your harmonic levels.',
    features: [
      { icon: BarChart3, label: '12 Characteristics' },
      { icon: TrendingUp, label: 'Harmonic Levels' },
    ],
    image:
      'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=600',
    imageAlt: 'Personality Metrics Graph',
    reverse: true,
  },
  {
    id: 'coach',
    badge: 'Support',
    title: 'Coach Appointment',
    description:
      "Sometimes you need more than self-reflection. Connect with certified coaches who can help you interpret your results and create actionable growth plans.",
    features: [
      { icon: Users, label: 'Expert Guidance' },
      { icon: Sparkles, label: 'Personalized Plans' },
    ],
    image:
      'https://images.pexels.com/photos/5717546/pexels-photo-5717546.jpeg?auto=compress&cs=tinysrgb&w=600',
    imageAlt: 'Coach Session',
    reverse: false,
  },
];

export function BenefitsSection() {
  return (
    <section id="features" className="py-20 md:py-28 px-4 md:px-8 bg-white">
      <div className="max-w-[1216px] mx-auto">
        <div className="text-center mb-16">
          <Badge variant="default" className="mb-4">
            Benefits
          </Badge>
          <h2 className="text-3xl md:text-heading-md font-bold text-brand-90 mb-4">
            Discover the real-life benefits our users love most.
          </h2>
          <p className="text-paragraph-xl text-gray-60 max-w-2xl mx-auto">
            Human Potential OS gives you tools to dive deep into your strengths,
            navigate challenges, and find clarity in just a few minutes a day.
          </p>
        </div>

        <div className="space-y-24">
          {benefits.map((benefit) => (
            <div
              key={benefit.id}
              className={`flex flex-col gap-12 items-center ${
                benefit.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'
              }`}
            >
              <div className="flex-1 w-full">
                <div className="relative">
                  <div className="absolute inset-0 bg-success-10 rounded-[32px] transform rotate-2" />
                  <div className="relative rounded-[32px] overflow-hidden shadow-soft-lg">
                    <img
                      src={benefit.image}
                      alt={benefit.imageAlt}
                      className="w-full h-[300px] md:h-[400px] object-cover"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-soft p-4 hidden md:flex items-center gap-3">
                    <div className="w-10 h-10 bg-success-10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-success-40" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-80">
                        Verified Insights
                      </p>
                      <p className="text-xs text-gray-40">
                        Science-backed methods
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1 w-full">
                <Badge
                  variant={
                    benefit.id === 'ai-companion'
                      ? 'teal'
                      : benefit.id === 'metrics'
                      ? 'orange'
                      : 'default'
                  }
                  className="mb-4"
                >
                  {benefit.badge}
                </Badge>
                <h3 className="text-2xl md:text-heading-sm font-bold text-brand-90 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-paragraph-lg text-gray-60 mb-6">
                  {benefit.description}
                </p>
                <div className="flex flex-wrap gap-4">
                  {benefit.features.map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-10 rounded-full"
                    >
                      <feature.icon className="w-4 h-4 text-success-40" />
                      <span className="text-sm font-medium text-gray-60">
                        {feature.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
